import React, { useState, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { parseCSVSchedule } from '../utils/csvParser';
import { useAuth } from '../context/AuthContext';
import { useSchedule } from '../context/ScheduleContext';
import '../styles/neumorphism.css';

const { FiX, FiUpload, FiFileText, FiCheck, FiAlertTriangle, FiLoader } = FiIcons;

function ScheduleUpload({ onClose, onSuccess }) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null); // 'parsing', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [viewMode, setViewMode] = useState('auto'); // 'auto', 'horizontal', 'vertical'
  const fileInputRef = useRef(null);
  const { currentDepartment } = useAuth();
  const { state } = useSchedule(); // Access existing employees

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file) => {
    if (!file.name.endsWith('.csv')) {
      setErrorMessage('Bitte wählen Sie eine CSV-Datei aus.');
      setUploadStatus('error');
      return;
    }
    setSelectedFile(file);
    setErrorMessage('');
    setUploadStatus(null);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setErrorMessage('Bitte wählen Sie zuerst eine Datei aus.');
      setUploadStatus('error');
      return;
    }

    if (!currentDepartment) {
      setErrorMessage('Keine Abteilung ausgewählt. Bitte wählen Sie eine Abteilung aus.');
      setUploadStatus('error');
      return;
    }

    setUploadStatus('parsing');
    setErrorMessage('');

    try {
      const text = await selectedFile.text();
      
      // Pass existing employees to parser for ID matching
      const result = parseCSVSchedule(text, currentDepartment.id, state.employees);

      if (!result || !result.schedule) {
        throw new Error('Die CSV-Datei konnte nicht verarbeitet werden.');
      }

      // Apply view mode if not auto
      if (viewMode !== 'auto') {
        result.schedule.viewMode = viewMode;
      }

      // Pass both schedule and the (potentially updated) employee list
      onSuccess({ schedule: result.schedule, employees: result.employees });
      setUploadStatus('success');
    } catch (error) {
      console.error('Upload error:', error);
      setErrorMessage(error.message || 'Fehler beim Verarbeiten der Datei.');
      setUploadStatus('error');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="neu-card bg-white max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Dienstplan hochladen</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 neu-button">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full neu-element bg-green-50 flex items-center justify-center">
              <SafeIcon icon={FiUpload} className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">CSV-Datei hochladen</h3>
            <p className="text-gray-600 mb-6">
              Laden Sie einen zuvor exportierten Dienstplan wieder hoch.
            </p>
          </div>

          {/* View Mode Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Ansichtsmodus</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setViewMode('auto')}
                className={`p-3 neu-button ${
                  viewMode === 'auto' ? 'bg-orange-50 text-orange-700' : 'bg-gray-50 text-gray-700'
                } text-sm`}
              >
                Auto
              </button>
              <button
                onClick={() => setViewMode('horizontal')}
                className={`p-3 neu-button ${
                  viewMode === 'horizontal' ? 'bg-orange-50 text-orange-700' : 'bg-gray-50 text-gray-700'
                } text-sm`}
              >
                Horizontal
              </button>
              <button
                onClick={() => setViewMode('vertical')}
                className={`p-3 neu-button ${
                  viewMode === 'vertical' ? 'bg-orange-50 text-orange-700' : 'bg-gray-50 text-gray-700'
                } text-sm`}
              >
                Vertikal
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              "Auto" erkennt den Modus automatisch aus der Datei.
            </p>
          </div>

          {/* File Upload Area */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragging ? 'border-orange-500 bg-orange-50' : 'border-gray-300 bg-gray-50'
            } hover:border-orange-400`}
          >
            <SafeIcon icon={FiFileText} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-sm text-gray-600">
              {selectedFile ? selectedFile.name : 'Ziehen Sie eine CSV-Datei hierher oder klicken Sie zum Auswählen'}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>

          {/* Status Messages */}
          {uploadStatus === 'parsing' && (
            <div className="text-center">
              <SafeIcon icon={FiLoader} className="w-8 h-8 text-orange-500 animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-600">Datei wird verarbeitet...</p>
            </div>
          )}

          {uploadStatus === 'error' && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg neu-element">
              <div className="flex items-center">
                <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-red-600 mr-2" />
                <p className="text-sm text-red-600">{errorMessage}</p>
              </div>
            </div>
          )}

          {uploadStatus === 'success' && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg neu-element">
              <div className="flex items-center">
                <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 mr-2" />
                <p className="text-sm text-green-600">Plan erfolgreich hochgeladen!</p>
              </div>
            </div>
          )}

          {/* Upload Button */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 py-2 px-4 neu-button text-gray-700"
              disabled={uploadStatus === 'parsing'}
            >
              Abbrechen
            </button>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploadStatus === 'parsing'}
              className="flex-1 py-2 px-4 neu-button bg-green-50 text-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Plan hochladen
            </button>
          </div>

          <div className="text-xs text-gray-500">
            <p>Hinweis: Die Datei muss im CSV-Format vorliegen und die Struktur des Excel-Exports haben.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScheduleUpload;